import {clamp, takeLast} from 'ramda';

import type {EntityHeaderAction} from '../../EntityHeader/types/EntityHeaderAction';

export type MovableHeaderAction = Extract<
  EntityHeaderAction,
  {type: 'button' | 'buttonWithTooltip' | 'dropdown-button'}
>;

type OverflowMenuAction = Extract<EntityHeaderAction, {type: 'dropdown-iconButton'}>;

type CompactHeaderActionSplit = {
  /** Actions that stay in the row, in their order. Without the page's own "more" menu once actions move. */
  shownActions: EntityHeaderAction[];
  /** Actions that move into the "more" menu, in their order. */
  overflowActions: MovableHeaderAction[];
  /** The page's own "more" menu, which takes the moved actions. */
  overflowMenu: OverflowMenuAction | undefined;
  /** The number of actions that can move. */
  movableCount: number;
};

// The primary action is the next step, and custom and form actions have no menu form, so they stay.
const isMovableAction = (action: EntityHeaderAction): action is MovableHeaderAction =>
  (action.type === 'button' ||
    action.type === 'buttonWithTooltip' ||
    action.type === 'dropdown-button') &&
  action.variant !== 'primary';

const isOverflowMenuAction = (action: EntityHeaderAction): action is OverflowMenuAction =>
  action.type === 'dropdown-iconButton';

/** Picks the actions of the collapsed record header that move into its "more" menu, from the end of the row. */
export function splitCompactHeaderActions(
  actions: EntityHeaderAction[],
  overflowCount: number
): CompactHeaderActionSplit {
  const movableActions = actions.filter(isMovableAction);
  const movedActions = takeLast(clamp(0, movableActions.length, overflowCount), movableActions);

  if (movedActions.length === 0) {
    return {
      shownActions: actions,
      overflowActions: [],
      overflowMenu: undefined,
      movableCount: movableActions.length,
    };
  }

  const overflowMenu = actions.find(isOverflowMenuAction);

  return {
    shownActions: actions.filter(
      (action) => action !== overflowMenu && !movedActions.some((moved) => moved === action)
    ),
    overflowActions: movedActions,
    overflowMenu,
    movableCount: movableActions.length,
  };
}
